import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Progress } from './ui/progress';
import { CheckCircle, XCircle, ArrowLeft, Trophy, RotateCcw } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useModuleRelease } from '../contexts/ModuleReleaseContext';
import { useReadingProgress } from '../contexts/ReadingProgressContext';

const Quiz = ({ quiz, moduleId, onComplete, onBack }) => {
  const { language } = useLanguage();
  const { markModuleCompleted } = useModuleRelease();
  const { getModuleReadingProgress } = useReadingProgress();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);

  const questions = quiz.questions;
  const totalQuestions = questions.length;
  const currentQ = questions[currentQuestion];
  
  // DEBUG: Log quiz state
  console.log(`🔍 QUIZ DEBUG: currentQuestion=${currentQuestion}, totalQuestions=${totalQuestions}, condition=${currentQuestion < totalQuestions - 1}`);

  const handleAnswerSelect = (optionIndex) => {
    setSelectedAnswers({
      ...selectedAnswers,
      [currentQuestion]: optionIndex
    });
    setShowExplanation(false);
  };

  const handleNext = () => {
    console.log(`🎯 QUIZ: handleNext called - currentQuestion=${currentQuestion}, totalQuestions=${totalQuestions}`);
    console.log(`🎯 QUIZ: Selected answer for question ${currentQuestion}:`, selectedAnswers[currentQuestion]);
    
    if (selectedAnswers[currentQuestion] !== undefined) {
      setShowExplanation(true);
      console.log(`🎯 QUIZ: Showing explanation, waiting 2 seconds...`);
      
      setTimeout(() => {
        const isLastQuestion = currentQuestion >= totalQuestions - 1;
        console.log(`🎯 QUIZ: After timeout - currentQuestion=${currentQuestion}, totalQuestions=${totalQuestions}, isLastQuestion=${isLastQuestion}`);
        
        if (!isLastQuestion) {
          console.log(`➡️ QUIZ: Moving to next question (${currentQuestion + 1})`);
          setCurrentQuestion(currentQuestion + 1);
          setShowExplanation(false);
        } else {
          console.log(`🏁 QUIZ: LAST QUESTION REACHED! Starting completion process...`);
          handleQuizCompletion();
        }
      }, 2000);
    } else {
      console.log(`❌ QUIZ: No answer selected for question ${currentQuestion}`);
    }
  };

  const calculateScore = () => {
    let correct = 0;
    questions.forEach((question, index) => {
      if (selectedAnswers[index] === question.correct) {
        correct++;
      }
    });
    return Math.round((correct / totalQuestions) * 100);
  };

  const handleQuizCompletion = async () => {
    console.log(`🎯🎯🎯 QUIZ COMPLETION STARTED for ${moduleId} 🎯🎯🎯`);
    console.log(`📊 QUIZ: Selected answers:`, selectedAnswers);
    console.log(`📊 QUIZ: Questions data:`, questions);
    
    try {
      setShowResults(true);
      console.log(`✅ QUIZ: Set showResults to true`);
      
      const score = calculateScore();
      console.log(`📊 QUIZ SCORE CALCULATED: ${score}%`);
      
      // Prepare quiz result data for backend
      const quizResult = {
        moduleId: moduleId,
        score: score,
        totalQuestions: totalQuestions,
        correctAnswers: Object.keys(selectedAnswers).filter(
          key => selectedAnswers[key] === questions[key].correct
        ).length,
        answers: questions.map((question, index) => ({
          questionId: question.id,
          selectedOption: selectedAnswers[index] !== undefined ? selectedAnswers[index] : -1,
          correct: selectedAnswers[index] === question.correct
        })),
        language: language
      };
      
      console.log(`📋 QUIZ: Prepared quiz result data:`, quizResult);
      
      // Call the parent completion handler
      console.log(`📡 QUIZ: CALLING onComplete callback with score ${score}%...`);
      onComplete(score, quizResult);
      console.log(`✅ QUIZ: onComplete callback executed successfully`);
      
    } catch (error) {
      console.error('❌ QUIZ COMPLETION ERROR:', error);
    }
  };



  const resetQuiz = () => {
    setCurrentQuestion(0);
    setSelectedAnswers({});
    setShowResults(false);
    setShowExplanation(false);
  };

  const getScoreMessage = (score) => {
    if (score >= 80) {
      return language === 'en' 
        ? 'Excellent! You have a great understanding of this topic.'
        : '¡Excelente! Tienes una gran comprensión de este tema.';
    } else if (score >= 60) {
      return language === 'en'
        ? 'Good job! You\'re on the right track. Consider reviewing the material again.'
        : '¡Buen trabajo! Vas por buen camino. Considera revisar el material nuevamente.';
    } else {
      return language === 'en'
        ? 'Keep learning! Review the module content and try again.'
        : '¡Sigue aprendiendo! Revisa el contenido del módulo e inténtalo de nuevo.';
    }
  };

  if (showResults) {
    const score = calculateScore();
    const correctAnswers = Object.keys(selectedAnswers).filter(
      key => selectedAnswers[key] === questions[key].correct
    ).length;

    return (
      <div className="max-w-2xl mx-auto">
        <Card className="bg-gradient-to-br from-green-50 to-blue-50 border-0 shadow-xl">
          <CardHeader className="text-center pb-4">
            <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center">
              <Trophy className="w-10 h-10 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold text-gray-800">
              {language === 'en' ? 'Quiz Complete!' : '¡Cuestionario Completado!'}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-6">
            <div className="space-y-4">
              <div className="text-4xl font-bold text-gray-800">{score}%</div>
              <p className="text-lg text-gray-600">
                {correctAnswers} {language === 'en' ? 'out of' : 'de'} {totalQuestions} {language === 'en' ? 'correct' : 'correctas'}
              </p>
              <p className="text-gray-600 max-w-md mx-auto">
                {getScoreMessage(score)}
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={resetQuiz}
                variant="outline"
                className="flex items-center space-x-2"
              >
                <RotateCcw className="w-4 h-4" />
                <span>{language === 'en' ? 'Try Again' : 'Intentar de Nuevo'}</span>
              </Button>
              <Button 
                onClick={onBack}
                className="flex items-center space-x-2"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>{language === 'en' ? 'Back to Module' : 'Volver al Módulo'}</span>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Progress Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <Button 
            onClick={onBack}
            variant="outline"
            size="sm"
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{language === 'en' ? 'Back' : 'Volver'}</span>
          </Button>
          <span className="text-sm text-gray-500">
            {currentQuestion + 1} / {totalQuestions}
          </span>
        </div>
        <Progress value={((currentQuestion + 1) / totalQuestions) * 100} className="h-2" />
      </div>

      {/* Quiz Header */}
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">{quiz.title[language]}</h1>
      </div>

      {/* Question Card */}
      <Card className="bg-white/90 backdrop-blur-sm border-0 shadow-xl mb-6">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-gray-800">
            {currentQ.question[language]}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {currentQ.options.map((option, index) => {
            const isSelected = selectedAnswers[currentQuestion] === index;
            const isCorrect = index === currentQ.correct;
            const showFeedback = showExplanation && selectedAnswers[currentQuestion] !== undefined;
            
            let buttonStyle = "w-full text-left p-4 border-2 transition-all duration-200 ";
            
            if (showFeedback) {
              if (isCorrect) {
                buttonStyle += "border-green-500 bg-green-50 text-green-800";
              } else if (isSelected && !isCorrect) {
                buttonStyle += "border-red-500 bg-red-50 text-red-800";
              } else {
                buttonStyle += "border-gray-300 bg-gray-50 text-gray-600";
              }
            } else if (isSelected) {
              buttonStyle += "border-orange-500 bg-orange-50 text-orange-800";
            } else {
              buttonStyle += "border-gray-300 hover:border-orange-300 hover:bg-orange-50";
            }

            return (
              <button
                key={index}
                onClick={() => !showExplanation && handleAnswerSelect(index)}
                className={buttonStyle}
                disabled={showExplanation}
              >
                <div className="flex items-center justify-between">
                  <span className="flex-1">{option[language]}</span>
                  {showFeedback && (
                    <div className="ml-4">
                      {isCorrect ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : isSelected ? (
                        <XCircle className="w-5 h-5 text-red-600" />
                      ) : null}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </CardContent>
      </Card>

      {/* Explanation */}
      {showExplanation && (
        <Card className="bg-blue-50 border-blue-200 mb-6">
          <CardContent className="pt-6">
            <p className="text-blue-800 leading-relaxed">
              <strong>{language === 'en' ? 'Explanation:' : 'Explicación:'}</strong> {currentQ.explanation[language]}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Next Button */}
      <div className="text-center">
        <Button 
          onClick={handleNext}
          disabled={selectedAnswers[currentQuestion] === undefined}
          size="lg"
          className="px-8 py-3 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {currentQuestion < totalQuestions - 1 
            ? (language === 'en' ? 'Next Question' : 'Siguiente Pregunta')
            : (language === 'en' ? 'Finish Quiz' : 'Finalizar Cuestionario')
          }
        </Button>
      </div>
    </div>
  );
};

export default Quiz;